import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAppSettings, updateAppSettings } from "@/api/admin.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Save, Sparkles, Coins } from "lucide-react";

const DEFAULT_IMAGE_PROMPT = `A professional food photography shot of "{{name}}"{{description}}. Slightly elevated angle on a modern minimalist table, restaurant quality presentation, soft natural lighting, shallow depth of field, appetizing and realistic. No text, no watermarks, no labels.`;

export function AppSettingsPage() {
  const qc = useQueryClient();
  const [imagePrompt, setImagePrompt] = useState("");
  const [costTranslate, setCostTranslate] = useState("");
  const [costImage, setCostImage] = useState("");
  const [costReimagine, setCostReimagine] = useState("");
  const [costDescription, setCostDescription] = useState("");

  const { data: settings } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: getAppSettings,
  });

  useEffect(() => {
    if (settings) {
      setImagePrompt(settings.ai_image_prompt || DEFAULT_IMAGE_PROMPT);
      setCostTranslate(settings.credit_cost_translate || "1");
      setCostImage(settings.credit_cost_image || "10");
      setCostReimagine(settings.credit_cost_reimagine || "15");
      setCostDescription(settings.credit_cost_description || "1");
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: () => updateAppSettings({
      ai_image_prompt: imagePrompt,
      credit_cost_translate: costTranslate,
      credit_cost_image: costImage,
      credit_cost_reimagine: costReimagine,
      credit_cost_description: costDescription,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-settings"] }),
  });

  return (
    <div className="max-w-2xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Uygulama Ayarlari</h1>
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          <Save className="mr-2 size-4" />
          {saveMutation.isPending ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </div>

      {/* AI Credit Costs */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Coins className="size-5" />
            AI Kredi Tutarlari
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Her AI isleminin kac kredi harcayacagini belirleyin.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Ceviri (dil basi)</Label>
              <Input type="number" min={0} value={costTranslate} onChange={(e) => setCostTranslate(e.target.value)} />
            </div>
            <div>
              <Label>Gorsel Olusturma</Label>
              <Input type="number" min={0} value={costImage} onChange={(e) => setCostImage(e.target.value)} />
            </div>
            <div>
              <Label>Gorsel Yenileme</Label>
              <Input type="number" min={0} value={costReimagine} onChange={(e) => setCostReimagine(e.target.value)} />
            </div>
            <div>
              <Label>Aciklama Olusturma</Label>
              <Input type="number" min={0} value={costDescription} onChange={(e) => setCostDescription(e.target.value)} />
            </div>
          </div>
          {saveMutation.isSuccess && <span className="text-sm text-green-600">Kaydedildi!</span>}
        </CardContent>
      </Card>

      {/* AI Image Prompt */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="size-5" />
            AI Gorsel Uretimi Prompt
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Yemek gorseli uretirken kullanilacak prompt. <code className="bg-muted px-1 rounded">{"{{name}}"}</code> urun adi, <code className="bg-muted px-1 rounded">{"{{description}}"}</code> aciklama ile degistirilir.
          </p>
          <textarea
            className="w-full min-h-32 rounded-lg border bg-background p-3 text-sm font-mono"
            value={imagePrompt}
            onChange={(e) => setImagePrompt(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setImagePrompt(DEFAULT_IMAGE_PROMPT)}>
              Varsayilana Dondur
            </Button>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <Label className="text-xs text-muted-foreground">Onizleme (Cappuccino icin):</Label>
            <p className="mt-1 text-sm">
              {imagePrompt.replace("{{name}}", "Cappuccino").replace("{{description}}", ", described as: Ozel blend kremali kahve")}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
